import { useState } from 'react'
import type { Client, CreateClientInput } from '@workflow/contracts'
import { Button, Field, TextField, cn } from '@workflow/ui'
import { errorSentence } from '@/lib/api/client'
import { useCreateClient } from '@/lib/api/hooks'
import { navigate } from '@/app/router'
import { PosterDialog } from '@/shell/PosterDialog'

const QUESTIONS = ['Who are they?', 'What do they call themselves?', 'How are they reached?'] as const

/**
 * Bringing a client in: one question at a time, floating over the registry.
 * The poster is the table the relationship starts at; the form asks only for
 * a name, then an address on the internet, then a person to reach — and the
 * rest (the Discord, the timezone, the words) waits for the client's own
 * page, where it can be filled in without a queue of empty fields watching.
 */
export function ClientCreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  /** Called instead of navigating when the dialog was opened over another one. */
  onCreated?: (client: Client) => void
}) {
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [contactPerson, setContactPerson] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const create = useCreateClient()

  const close = () => {
    onClose()
    setStep(0)
    setName('')
    setCompanyName('')
    setWebsiteUrl('')
    setContactPerson('')
    setContactEmail('')
    setContactPhone('')
  }

  const submit = () => {
    const input: CreateClientInput = { name: name.trim() }
    if (companyName.trim()) input.companyName = companyName.trim()
    if (websiteUrl.trim()) input.websiteUrl = websiteUrl.trim()
    if (contactPerson.trim()) input.contactPerson = contactPerson.trim()
    if (contactEmail.trim()) input.contactEmail = contactEmail.trim()
    if (contactPhone.trim()) input.contactPhone = contactPhone.trim()
    create.mutate(input, {
      onSuccess: (data) => {
        const client = data as Client
        close()
        // The registry's own button opens the new record; a dialog stacked
        // over another one simply takes the answer it came for.
        if (onCreated) onCreated(client)
        else navigate({ name: 'client', id: client.id })
      },
    })
  }

  const advance = () => {
    if (step < QUESTIONS.length - 1) setStep(step + 1)
    else submit()
  }

  const canAdvance = step === 0 ? name.trim() !== '' : true

  return (
    <PosterDialog
      open={open}
      onClose={close}
      labelledBy="client-create-title"
      image="/art/onboarding-shoreline.png"
      colorArtwork
      alt="A blue shoreline painting with orange flowers"
      headline="New Client"
      caps="Workflow · New Client"
    >
      <form
        className="flex min-h-[24rem] flex-col px-6 py-6 sm:px-8 sm:py-7 lg:px-10"
        onSubmit={(event) => {
          event.preventDefault()
          if (canAdvance) advance()
        }}
      >
        <div className="flex items-center justify-between gap-4">
          <p className="text-micro text-muted-foreground uppercase">
            New Client · Step {step + 1} of {QUESTIONS.length}
          </p>
          <div className="flex gap-1.5" aria-hidden>
            {QUESTIONS.map((_, index) => (
              <span
                key={index}
                className={cn('h-0.5 w-5 rounded-full transition-colors duration-fast', index <= step ? 'bg-foreground' : 'bg-tint')}
              />
            ))}
          </div>
        </div>
        <h3 id="client-create-title" className="mt-3 font-display text-2xl text-foreground">
          {QUESTIONS[step]}
        </h3>
        <p className="mt-2 text-caption text-muted-foreground">
          {step === 0
            ? "The client's name."
            : step === 1
              ? 'The company behind the name, if there is one — and where it lives on the web.'
              : 'One person is enough. The rest of the record can wait for their page.'}
        </p>

        <div className="mt-6 flex flex-1 flex-col justify-start gap-4">
          {step === 0 ? (
            <Field label="Name" htmlFor="client-create-name">
              <TextField
                id="client-create-name"
                value={name}
                autoFocus
                placeholder="The name the studio uses for them"
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
          ) : step === 1 ? (
            <>
              <Field label="Company name" htmlFor="client-create-company">
                <TextField
                  id="client-create-company"
                  value={companyName}
                  autoFocus
                  placeholder="The name they call themselves"
                  onChange={(event) => setCompanyName(event.target.value)}
                />
              </Field>
              <Field label="Website" htmlFor="client-create-website">
                <TextField
                  id="client-create-website"
                  value={websiteUrl}
                  placeholder="https://"
                  onChange={(event) => setWebsiteUrl(event.target.value)}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Contact person" htmlFor="client-create-person">
                <TextField
                  id="client-create-person"
                  value={contactPerson}
                  autoFocus
                  placeholder="Who the studio talks to"
                  onChange={(event) => setContactPerson(event.target.value)}
                />
              </Field>
              <Field label="Contact email" htmlFor="client-create-email">
                <TextField
                  id="client-create-email"
                  value={contactEmail}
                  placeholder="name@company.com"
                  onChange={(event) => setContactEmail(event.target.value)}
                />
              </Field>
              <Field label="Contact phone" htmlFor="client-create-phone">
                <TextField
                  id="client-create-phone"
                  value={contactPhone}
                  placeholder="Where they pick up"
                  onChange={(event) => setContactPhone(event.target.value)}
                />
              </Field>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center gap-3">
          {step > 0 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
          )}
          <span className="flex-1" />
          <Button type="submit" variant="primary" busy={create.isPending} disabled={!canAdvance}>
            {step === QUESTIONS.length - 1 ? 'Add to the registry' : 'Continue'}
          </Button>
        </div>
        {create.error ? <p className="mt-3 text-caption text-danger">{errorSentence(create.error)}</p> : null}
      </form>
    </PosterDialog>
  )
}
